import { useState, useEffect, PropsWithChildren } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { useTheme } from 'styled-components';
import { createPaymentIntent } from 'src/interviews/requests/public_site/stripe';
import getStripe from './utils/get-stripejs';
import LoadingContainer from 'src/interviews/components/LoadingContainer';
import useDoctorConfirmationContext from '../../useDoctorConfirmationContext';
import { PRODUCT_DESCRIPTION, STRIPE_PUBLISHABLE_KEY } from './constants';

export default function StripeProvider({
  onError,
  children,
}: PropsWithChildren<{
  onError: (isError: boolean) => void;
}>) {
  const [clientSecret, setClientSecret] = useState<string>();
  const [isLoading, setIsLoading] = useState(true);
  const { setCustomerId, chargeAmount } = useDoctorConfirmationContext();

  const theme = useTheme();
  const stripePromise = getStripe(STRIPE_PUBLISHABLE_KEY);

  useEffect(() => {
    // Create Payment Intent as soon as this component loads
    createPaymentIntent({ productDescription: PRODUCT_DESCRIPTION, amount: chargeAmount })
      .then((data) => {
        if (data) {
          setClientSecret(data?.clientSecret);
          setCustomerId(data?.customerId);
          setIsLoading(false);
        }
      })
      .catch(() => {
        onError(true);
        setIsLoading(false);
      });
  }, []);

  if (isLoading) {
    return <LoadingContainer />;
  }

  return (
    <Elements
      options={{
        clientSecret,
        appearance: {
          theme: 'stripe',
          variables: {
            colorPrimary: theme.palette.primary.main,
            colorText: theme.palette.text.primary,
            colorDanger: theme.palette.status.error,
            fontFamily: theme.typography.FontFamily.Primary,
            borderRadius: '4px',
            fontSizeBase: '16px',
            colorTextPlaceholder: theme.palette.gray[60],
          },
        },
        fonts: [
          {
            family: 'GT Eesti Display',
            src: 'url(https://static.buoyhealth.com/fonts/GT-Eesti-Display-Regular.woff2)',
            weight: '400',
          },
        ],
      }}
      stripe={stripePromise}
    >
      {children}
    </Elements>
  );
}
